import React, { useEffect, useMemo, useState } from 'react';
import { ui } from '../../ui';
import { RepTimelineResponse, RepTimelineRep, getRepTimeline } from '../../api';
import { LoadingSpinner } from '../../../../components/common/LoadingSpinner';

interface RepTimelineProps {
  date: string;
  onDateChange: (date: string) => void;
  // Lets the "map in Agents tab" note for unmapped reps jump straight to that tab.
  onGoToAgentsTab?: () => void;
}

// Default visible window (7a-9p ET); expands to fit any shift/punch/call data that falls
// outside it (FR-2.1's "data-driven expansion").
const DEFAULT_WINDOW_START = 7 * 60;
const DEFAULT_WINDOW_END = 21 * 60;

// BUG-5: a fixed pixel floor for the swimlane/hour-ruler track, wrapped in overflow-x-auto
// (below), so narrow viewports scroll horizontally instead of compressing labels/segments
// to illegibility — mirrors the ui.tableWrap convention used by this feature's tables.
const TIMELINE_MIN_WIDTH = 700;

// 'HH:mm' or 'HH:mm:ss' -> minutes since midnight.
const toMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
// End <= Start means an overnight wrap (mirrors ScheduleShift / CoverageException convention).
const spanEndMinutes = (startET: string, endET: string) => {
  const start = toMinutes(startET);
  const end = toMinutes(endET);
  return end <= start ? end + 1440 : end;
};
const fmtHourLabel = (minutes: number) => {
  const h = Math.floor(minutes / 60) % 24;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${h < 12 ? 'a' : 'p'}`;
};
// "13:45:00" -> "1:45 PM"
const fmtClock = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const fmtDur = (seconds: number | null) => {
  if (seconds == null) return 'duration unknown';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// FR Group 2 — one horizontal swimlane per mapped rep for a single ET calendar day: a light
// Planned (schedule) band, a darker Actual (punch) band, and call marks sized by duration
// (a diamond point-marker when duration is unknown). Bands differ by height/border as well as
// color so the distinction doesn't rely on color alone. No drag/zoom — a fixed axis window
// that expands only when the data needs more room.
export const RepTimeline: React.FC<RepTimelineProps> = ({ date, onDateChange, onGoToAgentsTab }) => {
  const [data, setData] = useState<RepTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<number | ''>('');

  const load = () => {
    setLoading(true);
    setError(null);
    return getRepTimeline(date)
      .then(setData)
      .catch(() => setError('Could not load the rep timeline — try again.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized so the window-bounds useMemo below keys on a stable reference, not a fresh [].
  const reps = useMemo(() => data?.Reps ?? [], [data]);
  const visibleReps = selectedAgentId === '' ? reps : reps.filter(r => r.AgentID === selectedAgentId);
  // BUG-4: reps with no Roster mapping render in their own "Unmapped reps" subsection below
  // the mapped swimlanes, not interleaved with them — renderRep already degrades gracefully
  // for them (empty Shifts/Punches), it's just grouped separately here.
  const mappedReps = visibleReps.filter(r => r.RosterCanonicalName != null);
  const unmappedReps = visibleReps.filter(r => r.RosterCanonicalName == null);

  const { windowStart, windowEnd } = useMemo(() => {
    const times: number[] = [];
    reps.forEach(r => {
      r.Shifts.forEach(s => { times.push(toMinutes(s.StartET), spanEndMinutes(s.StartET, s.EndET)); });
      r.Punches.forEach(p => { times.push(toMinutes(p.StartET), spanEndMinutes(p.StartET, p.EndET)); });
      r.Calls.forEach(c => {
        const start = toMinutes(c.StartET);
        times.push(start, start + (c.DurationSeconds != null ? c.DurationSeconds / 60 : 0));
      });
    });
    return {
      windowStart: times.length ? Math.min(DEFAULT_WINDOW_START, ...times) : DEFAULT_WINDOW_START,
      windowEnd: times.length ? Math.max(DEFAULT_WINDOW_END, ...times) : DEFAULT_WINDOW_END,
    };
  }, [reps]);

  const span = Math.max(1, windowEnd - windowStart);
  const pct = (minutes: number) => Math.min(100, Math.max(0, ((minutes - windowStart) / span) * 100));

  const hourTicks: number[] = [];
  for (let m = Math.ceil(windowStart / 60) * 60; m <= windowEnd; m += 60) hourTicks.push(m);

  const renderRep = (rep: RepTimelineRep) => (
    <div key={rep.AgentID} className="border-b border-border-default py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-primary">{rep.DisplayName}</span>
        <span className="text-xs text-muted">
          {rep.Shifts.length} shift{rep.Shifts.length === 1 ? '' : 's'} · {rep.Punches.length} punch{rep.Punches.length === 1 ? '' : 'es'} · {rep.Calls.length} call{rep.Calls.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="relative h-8 rounded bg-surface" style={{ minWidth: '100%' }}>
        {rep.Shifts.map((s, i) => (
          <div
            key={`shift-${i}`}
            title={`Scheduled ${fmtClock(s.StartET)}–${fmtClock(s.EndET)} ET`}
            className="absolute top-1 h-1.5 rounded-sm border border-dashed border-blue-400 bg-blue-300/30"
            style={{ left: `${pct(toMinutes(s.StartET))}%`, width: `${Math.max(0.3, pct(spanEndMinutes(s.StartET, s.EndET)) - pct(toMinutes(s.StartET)))}%` }}
          />
        ))}
        {rep.Punches.map((p, i) => (
          <div
            key={`punch-${i}`}
            title={`Punched ${fmtClock(p.StartET)}–${fmtClock(p.EndET)} ET`}
            className="absolute top-3.5 h-2.5 rounded-sm border border-solid border-blue-700 bg-blue-600/70"
            style={{ left: `${pct(toMinutes(p.StartET))}%`, width: `${Math.max(0.3, pct(spanEndMinutes(p.StartET, p.EndET)) - pct(toMinutes(p.StartET)))}%` }}
          />
        ))}
        {rep.Calls.map(c => {
          const start = toMinutes(c.StartET);
          if (c.DurationSeconds == null) {
            return (
              <div
                key={c.CallID}
                title={`Call at ${fmtClock(c.StartET)} ET — duration unknown`}
                className="absolute top-6 h-2 w-2 rotate-45 border border-amber-700 bg-amber-500"
                style={{ left: `calc(${pct(start)}% - 4px)` }}
              />
            );
          }
          const widthPct = pct(start + c.DurationSeconds / 60) - pct(start);
          return (
            <div
              key={c.CallID}
              title={`Call ${fmtClock(c.StartET)} ET — ${fmtDur(c.DurationSeconds)}`}
              className="absolute top-6 h-2 rounded-sm bg-emerald-600"
              style={{ left: `${pct(start)}%`, width: `${Math.max(0.3, widthPct)}%`, minWidth: '2px' }}
            />
          );
        })}
      </div>
      <details className="mt-1">
        <summary className="cursor-pointer text-xs text-muted">Text summary</summary>
        <ul className="mt-1 space-y-0.5 text-xs text-secondary">
          {rep.Shifts.map((s, i) => <li key={`s-${i}`}>Scheduled: {fmtClock(s.StartET)}–{fmtClock(s.EndET)} ET</li>)}
          {rep.Punches.map((p, i) => <li key={`p-${i}`}>Punched: {fmtClock(p.StartET)}–{fmtClock(p.EndET)} ET</li>)}
          {rep.Calls.map(c => <li key={c.CallID}>Call: {fmtClock(c.StartET)} ET — {fmtDur(c.DurationSeconds)}</li>)}
          {rep.Shifts.length === 0 && rep.Punches.length === 0 && rep.Calls.length === 0 && <li>No activity.</li>}
        </ul>
      </details>
    </div>
  );

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-secondary">Rep Timeline</h3>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className={ui.label} htmlFor="rep-timeline-date">Date (ET)</label>
          <input
            id="rep-timeline-date"
            type="date"
            className={ui.control}
            value={date}
            onChange={e => onDateChange(e.target.value)}
          />
        </div>
        <div>
          <label className={ui.label} htmlFor="rep-timeline-agent">Rep</label>
          <select
            id="rep-timeline-agent"
            className={ui.control}
            value={selectedAgentId}
            onChange={e => setSelectedAgentId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">All reps</option>
            {reps.map(r => <option key={r.AgentID} value={r.AgentID}>{r.DisplayName}</option>)}
          </select>
        </div>
        <button className={ui.btnSecondary} onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div aria-live="polite" role="status" className="sr-only">
        {loading ? 'Loading rep timeline…' : ''}
      </div>

      {loading && <LoadingSpinner size="sm" message="Loading rep timeline…" />}
      {!loading && error && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          {/* Legend — shapes/heights differ, not just color, per the a11y note above. */}
          <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-secondary">
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-4 rounded-sm border border-dashed border-blue-400 bg-blue-300/30" /> Scheduled
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2.5 w-4 rounded-sm border border-solid border-blue-700 bg-blue-600/70" /> Punched
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-4 rounded-sm bg-emerald-600" /> Call
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rotate-45 border border-amber-700 bg-amber-500" /> Call, duration unknown
            </span>
          </div>

          {mappedReps.length === 0 && unmappedReps.length === 0 && data.UnmappedAgentNames.length === 0 && (
            <p className="py-6 text-center text-sm text-muted">No reps with activity for this day.</p>
          )}

          {/* BUG-5: fixed min-width track in a horizontal-scroll wrapper so narrow viewports
              scroll instead of compressing the hour ruler / swimlanes to illegibility. */}
          {(mappedReps.length > 0 || unmappedReps.length > 0) && (
            <div className="overflow-x-auto rounded-lg border border-border-default">
              <div style={{ minWidth: `${TIMELINE_MIN_WIDTH}px` }} className="p-2">
                {/* Hour axis ruler */}
                <div className="relative mb-1 h-4 text-xs text-muted">
                  {hourTicks.map(m => (
                    <span key={m} className="absolute -translate-x-1/2" style={{ left: `${pct(m)}%` }}>{fmtHourLabel(m)}</span>
                  ))}
                </div>

                {mappedReps.map(renderRep)}

                {/* BUG-4: every unmapped rep — whether it has a Reps[] entry with
                    RosterCanonicalName null, or is only a name in UnmappedAgentNames (no
                    Agent record to draw a swimlane for) — is grouped in this one subsection,
                    not interleaved with the mapped swimlanes above. */}
                {unmappedReps.length > 0 && (
                  <div className="mt-4 border-t border-border-default pt-3">
                    <h4 className="mb-2 text-sm font-medium text-primary">Unmapped reps</h4>
                    {unmappedReps.map(renderRep)}
                  </div>
                )}
              </div>
            </div>
          )}

          {(unmappedReps.length > 0 || data.UnmappedAgentNames.length > 0) && (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <p className="font-medium">Unmapped reps — schedule/punch data unavailable</p>
              <p className="mt-1">
                {[...unmappedReps.map(r => r.DisplayName), ...data.UnmappedAgentNames].join(', ')}.
                Schedule and punch data isn't available for these reps until they're paired to a
                roster name.{' '}
                {onGoToAgentsTab ? (
                  <button type="button" className="underline hover:no-underline" onClick={onGoToAgentsTab}>
                    Map in Agents tab
                  </button>
                ) : (
                  'Map them in the Agents tab.'
                )}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
